import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useLazyCurrentUserIndexQuery } from "~/store/api";
import { setHasLoadedInitial } from "~/store/authentication";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { useAuthN } from "./useAuthN";

interface UseLoadCurrentUser {
  hasLoadedInitial: boolean;
}

export const useLoadCurrentUser = (): UseLoadCurrentUser => {
  const hasLoadedInitial = useAppSelector(
    (state) => state.authentication.hasLoadedInitial,
  );
  const isPotentiallyAuthenticated = useAppSelector(
    (state) => state.authentication.isPotentiallyAuthenticated,
  );
  const dispatch = useAppDispatch();
  const [apiCurrentUserIndex] = useLazyCurrentUserIndexQuery();
  const { handleSetUser, handleUserSignOut } = useAuthN();

  useEffect(() => {
    if (hasLoadedInitial) return;
    if (!isPotentiallyAuthenticated) {
      dispatch(setHasLoadedInitial(true));
      return;
    }

    (async () => {
      try {
        const responseBody = await apiCurrentUserIndex().unwrap();
        const currentUser = responseBody.data.attributes;

        handleSetUser(currentUser);
      } catch (err: any) {
        if (err?.status === 401) {
          Sentry.captureMessage(
            `User not authenticated; signing out and redirecting; error: ${err.status} ${err.message}}`,
            "info",
          );
          return handleUserSignOut();
        } else {
          Sentry.captureException(err);
        }
      } finally {
        dispatch(setHasLoadedInitial(true));
      }
    })();
  }, [
    apiCurrentUserIndex,
    dispatch,
    handleSetUser,
    handleUserSignOut,
    hasLoadedInitial,
    isPotentiallyAuthenticated,
  ]);

  return { hasLoadedInitial };
};
